import * as React from "react";
import DialogContext from "./DialogContext";

export const useDialog = () => {
  const { setDialog, closeDialogById } = React.useContext(DialogContext);

  const closeDialog = React.useCallback(
    (id: string | number) => closeDialogById(id),
    [closeDialogById]
  );

  const closePopup = React.useCallback(
    (id: string | number) => closeDialogById(id),
    [closeDialogById]
  );

  type ConfirmType = {
    text?: string;
    [key: string]: any;
  };

  /**
   * Setups configuration to show confirmation and clears after.
   * If cancel or confirm is not provided a promise,
   * that is rejected if cancel and resolved if confirmed,
   * is returned
   */
  const showConfirm = React.useCallback(
    async ({ id = Date.now(), ...props }: ConfirmType) => {
      try {
        return await new Promise((resolve) => {
          setDialog({
            ...props,
            id,
            confirm: () => {
              resolve(true);
            },
            cancel: () => {
              resolve(false);
            },
          });
        });
      } finally {
        closeDialog(id);
      }
    },
    [setDialog, closeDialog]
  );

  type DialogType = {
    text?: string;
    [key: string]: any;
  };
  /**
   * Setups configuration to show alert and clears after.
   * If cancel or confirm is not provided a promise,
   * that is resolved on confirm, is returned
   */
  const showAlert = React.useCallback(
    async ({ id = Date.now(), ...props }: DialogType) => {
      try {
        return await new Promise((resolve) => {
          setDialog({ ...props, confirm: props.confirm ?? resolve, id });
        });
      } finally {
        closeDialog(id);
      }
    },
    [closeDialog, setDialog]
  );

  /**
   * Setups configuration to show dialog and clears after.
   */
  const showPopup = React.useCallback(
    async ({ id = Date.now(), ...props }) => {
      return new Promise((resolve) => {
        setDialog({
          ...props,
          id,
          type: "popup",
          close: resolve,
        });
      }).finally(() => {
        closePopup(id);
      });
    },
    [setDialog, closePopup]
  );

  return { showAlert, showConfirm, showPopup, closePopup, closeDialog };
};
